import { Chip, Divider, Stack, Typography } from '@mui/material';
import { FareUpdatedNotificationDifference, formatName } from '@silbo/core';
import { colors } from '@silbo/ui';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { formatAge } from '../../../utils/format-age';

type FareUpdatedDifferencesProps = {
  differences: FareUpdatedNotificationDifference[];
};

const FareUpdatedDifferences = ({ differences }: FareUpdatedDifferencesProps) => {
  const { t } = useTranslation('patient-transport');
  return (
    <Stack spacing={2} divider={<Divider sx={{ borderColor: colors.blue[10] }} />}>
      {differences.map((difference, index) => {
        const renderNewValue = () => {
          switch (difference.type) {
            case 'fromLocation':
            case 'toLocation':
              return (
                <Typography fontWeight={600}>
                  {difference.newValue.unit}
                  {difference.newValue.areasBreadcrumb && ` - ${difference.newValue.areasBreadcrumb}`}
                </Typography>
              );

            case 'patient':
              return (
                <Stack>
                  <Stack direction="row" alignItems="center" gap={2}>
                    <Typography>{difference.newValue.gender === 'FEMALE' ? '👩' : '👨'}</Typography>
                    <Typography fontWeight={600}>
                      {formatName(difference.newValue.firstName, difference.newValue.lastName)}
                    </Typography>
                  </Stack>
                  {difference.newValue.birthdate && (
                    <Typography fontWeight={600}>
                      {`${formatAge(difference.newValue.birthdate)} (${format(difference.newValue.birthdate, 'dd/MM/yyyy')})`}
                    </Typography>
                  )}
                </Stack>
              );

            case 'position':
              return <Typography fontWeight={600}>{t(`farePositions.${difference.newValue}`)}</Typography>;
            case 'comment':
              return <Typography fontWeight={600}>{difference.newValue}</Typography>;

            case 'specificities':
              return (
                <Stack gap={1} direction="row" flexWrap="wrap">
                  {difference.newValue.length > 0 ? (
                    difference.newValue.map((specificity) => (
                      <Chip variant="rounded" color="secondary" size="x-small" key={specificity} label={specificity} />
                    ))
                  ) : (
                    <Typography fontWeight={600}>{t('fareUpdatedToast.differences.specificitiesRemoved')}</Typography>
                  )}
                </Stack>
              );

            default:
              return null;
          }
        };

        return (
          <Stack key={index} spacing={1}>
            <Typography>{t(`fareUpdatedToast.differences.${difference.type}`)}</Typography>
            {renderNewValue()}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default FareUpdatedDifferences;
